// shared-lib/group-customization/fixedNodes.js
//
// Fixed nodes for the group solver: every opening / wall ref a group's links
// name, resolved to a pose in the group's ANCHOR-LOCAL frame. Openings are
// matched by STRUCTURE id (`canonicalOpeningId`) after hydrating the room's
// slim copies against the structure arrays; without those arrays a room copy
// that still carries its own `center` (legacy data) is used, and anything
// else is simply absent — the solver then ignores that member, it never
// guesses (reference-docs/layout-relationships §2.2, §3.1 stage 1).
//
// Moved here from `web-app/src/utils/roomGeometryRefs.js` (which re-exports
// it) so the server's group settle (`settleGroups.js`) reads the same nodes.

const { worldToLocal } = require('./transforms');
const { refKey, isFixedRef, normalizeRef } = require('./refs');
const {
  hydrateRoomOpenings,
  canonicalOpeningId,
} = require('../estimate-pricing/utils/openingSyncUtils');

function fixedRefsOf(links) {
  const out = new Map();
  for (const l of links || []) {
    const refs = [
      ...(l?.fixedMembers || []),
      ...(l?.axis ? [l.axis] : []),
      ...(l?.wall ? [l.wall] : []),
      ...(l?.target ? [l.target] : []),
    ];
    for (const r of refs) {
      let n;
      try {
        n = normalizeRef(r);
      } catch (e) {
        continue;
      }
      if (n && isFixedRef(n)) out.set(refKey(n), n);
    }
  }
  return out;
}

function openingsOf(room, structureOpenings) {
  const src = structureOpenings || {};
  const hydrated = hydrateRoomOpenings(room, {
    doors: src.doors || [],
    windows: src.windows || [],
    openings: src.openings || [],
  });
  return [
    ...(hydrated?.doors || []),
    ...(hydrated?.windows || []),
    ...(hydrated?.openings || []),
  ].filter((o) => o?.center);
}

function openingPoint(opening, ref) {
  const c = opening.center;
  if (!ref.edge || ref.edge === 'center') return c;
  // Edge along the opening's own wall direction: the wall it hangs on.
  const half = (opening.dimensions?.x || 0) / 2;
  const rotY = opening.rotation?.y || 0;
  const dir = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  const sign = ref.edge === 'left' ? -1 : 1;
  return { x: c.x + sign * half * dir.x, y: c.y, z: c.z + sign * half * dir.z };
}

function wallPoint(wall, ref) {
  const bl = wall?.alignment?.bottomLeft;
  const br = wall?.alignment?.bottomRight;
  if (!bl || !br) return null;
  const at = ref.at || 'center';
  const p =
    at === 'start'
      ? bl
      : at === 'end'
        ? br
        : { x: (bl.x + br.x) / 2, z: (bl.z + br.z) / 2 };
  return { x: p.x, y: wall.center?.y ?? 0, z: p.z };
}

/**
 * @param {Object[]} links — the group's links (room-level + legacy)
 * @param {Object} anchor — the group's anchor (world space)
 * @param {Object} room — room-store room (walls, slim opening copies)
 * @param {{doors?, windows?, openings?}} [structureOpenings]
 * @returns {Map<string, {dx,dy,dz,rotYRel}>} refKey → anchor-local pose
 */
function fixedNodesForLinks(links, anchor, room, structureOpenings) {
  const nodes = new Map();
  const refs = fixedRefsOf(links);
  if (!refs.size || !anchor?.center) return nodes;
  const openings = openingsOf(room, structureOpenings);
  for (const [key, ref] of refs) {
    let point = null;
    if (ref.kind === 'opening') {
      const o = openings.find((x) => canonicalOpeningId(x) === ref.id);
      if (o) point = openingPoint(o, ref);
    } else if (ref.kind === 'wall') {
      const w = (room?.walls || []).find((x) => x?.id === ref.id);
      if (w) point = wallPoint(w, ref);
    }
    if (!point) continue;
    const local = worldToLocal(point, anchor);
    nodes.set(key, { dx: local.dx, dy: local.dy, dz: local.dz, rotYRel: 0 });
  }
  return nodes;
}

module.exports = { fixedNodesForLinks };
